import React, { useState, useEffect } from "react";
import { useTimer } from "../hooks/useTimer";
import { useToast } from "../hooks/useToast";

interface TimerProps {
  enabled: boolean;
  onTimerComplete: () => void;
  isMetronomeRunning: boolean;
  onMetronomeStop: () => void;
}

export const Timer: React.FC<TimerProps> = ({
  enabled,
  onTimerComplete,
  isMetronomeRunning,
  onMetronomeStop,
}) => {
  const { showError } = useToast();
  const [isEditingMinutes, setIsEditingMinutes] = useState(false);
  const [isEditingSeconds, setIsEditingSeconds] = useState(false);
  const [minutesInput, setMinutesInput] = useState("");
  const [secondsInput, setSecondsInput] = useState("");

  const {
    minutes,
    seconds,
    displayMinutes,
    displaySeconds,
    isRunning,
    setMinutes,
    setSeconds,
    start,
    stop,
    reset,
  } = useTimer(1, 0, enabled, onTimerComplete);

  // Update input values when timer values change
  useEffect(() => {
    if (!isEditingMinutes) {
      setMinutesInput(displayMinutes);
    }
  }, [displayMinutes, isEditingMinutes]);

  useEffect(() => {
    if (!isEditingSeconds) {
      setSecondsInput(displaySeconds);
    }
  }, [displaySeconds, isEditingSeconds]);

  // Start timer when metronome starts
  useEffect(() => {
    if (enabled && isMetronomeRunning && !isRunning) {
      start();
    }
  }, [enabled, isMetronomeRunning, isRunning, start]);

  // Stop timer when metronome stops
  useEffect(() => {
    if (!isMetronomeRunning && isRunning) {
      stop();
      reset();
    }
  }, [isMetronomeRunning, isRunning, stop, reset]);

  // Stop and reset timer when timer is disabled
  useEffect(() => {
    if (!enabled && isRunning) {
      stop();
      reset();
    }
  }, [enabled, isRunning, stop, reset]);

  const validateAndSetMinutes = (value: string) => {
    let numValue = parseInt(value);

    if (isNaN(numValue) || value === "") {
      numValue = minutes;
    } else {
      const originalValue = numValue;
      if (numValue < 0) numValue = 0;
      if (numValue > 59) numValue = 59;
      if (numValue !== originalValue) {
        showError("Enter minutes in the range of 0 to 59");
      }
    }

    setMinutes(numValue);
    setMinutesInput(numValue.toString().padStart(2, "0"));
    setIsEditingMinutes(false);
  };

  const validateAndSetSeconds = (value: string) => {
    let numValue = parseInt(value);

    if (isNaN(numValue) || value === "") {
      numValue = seconds;
    } else {
      const originalValue = numValue;
      if (numValue < 0) numValue = 0;
      if (numValue > 59) numValue = 59;
      if (numValue !== originalValue) {
        showError("Enter seconds in the range of 0 to 59");
      }
    }

    setSeconds(numValue);
    setSecondsInput(numValue.toString().padStart(2, "0"));
    setIsEditingSeconds(false);
  };

  const handleMinutesClick = () => {
    if (isMetronomeRunning) {
      showError("Cannot edit timer while metronome is running");
      return;
    }
    setIsEditingMinutes(true);
    setMinutesInput(minutes.toString().padStart(2, "0"));
  };

  const handleSecondsClick = () => {
    if (isMetronomeRunning) {
      showError("Cannot edit timer while metronome is running");
      return;
    }
    setIsEditingSeconds(true);
    setSecondsInput(seconds.toString().padStart(2, "0"));
  };

  const handleMinutesFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  const handleSecondsFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d{0,2}$/.test(value)) {
      setMinutesInput(value);
    }
  };

  const handleSecondsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d{0,2}$/.test(value)) {
      setSecondsInput(value);
    }
  };

  const handleMinutesBlur = () => {
    validateAndSetMinutes(minutesInput);
  };

  const handleSecondsBlur = () => {
    validateAndSetSeconds(secondsInput);
  };

  const handleMinutesKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      validateAndSetMinutes(minutesInput);
    } else if (e.key === "Escape") {
      setMinutesInput(displayMinutes);
      setIsEditingMinutes(false);
    }
  };

  const handleSecondsKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      validateAndSetSeconds(secondsInput);
    } else if (e.key === "Escape") {
      setSecondsInput(displaySeconds);
      setIsEditingSeconds(false);
    }
  };

  if (!enabled) {
    return null;
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        marginTop: "20px",
        marginBottom: "20px",
        fontSize: "32px",
        fontWeight: "500",
        color: "var(--text-primary)",
        // fontFamily: "monospace",
        letterSpacing: "2px",
      }}
    >
      {isEditingMinutes ? (
        <input
          type="text"
          inputMode="numeric"
          value={minutesInput}
          onChange={handleMinutesChange}
          onBlur={handleMinutesBlur}
          onFocus={handleMinutesFocus}
          onKeyDown={handleMinutesKeyDown}
          autoFocus
          autoComplete="off"
          style={{
            background: "transparent",
            border: "none",
            color: "var(--text-primary)",
            fontSize: "32px",
            fontWeight: "500",
            fontFamily: "inherit",
            textAlign: "center",
            width: "60px",
            outline: "none",
            caretColor: "var(--text-primary)",
            letterSpacing: "2px",
            padding: "4px 8px",
            margin: "-4px -8px",
            boxSizing: "border-box",
            verticalAlign: "baseline",
            lineHeight: "1",
            display: "inline-block",
          }}
        />
      ) : (
        <span
          onClick={handleMinutesClick}
          style={{
            cursor: isMetronomeRunning ? "default" : "pointer",
            userSelect: isMetronomeRunning ? "none" : "auto",
            transition: "all 0.2s ease-out",
            display: "inline-block",
            transformOrigin: "center",
            padding: "4px 8px",
            borderRadius: "8px",
            margin: "-4px -8px",
          }}
          onMouseEnter={(e) => {
            if (!isMetronomeRunning) {
              e.currentTarget.style.backgroundColor = "var(--button-hover-bg)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          {displayMinutes}
        </span>
      )}

      <span
        style={{
          margin: "0 8px",
          userSelect: isMetronomeRunning ? "none" : "auto",
        }}
      >
        :
      </span>

      {isEditingSeconds ? (
        <input
          type="text"
          inputMode="numeric"
          value={secondsInput}
          onChange={handleSecondsChange}
          onBlur={handleSecondsBlur}
          onFocus={handleSecondsFocus}
          onKeyDown={handleSecondsKeyDown}
          autoFocus
          autoComplete="off"
          style={{
            background: "transparent",
            border: "none",
            color: "var(--text-primary)",
            fontSize: "32px",
            fontWeight: "500",
            fontFamily: "inherit",
            textAlign: "center",
            width: "60px",
            outline: "none",
            caretColor: "var(--text-primary)",
            letterSpacing: "2px",
            padding: "4px 8px",
            margin: "-4px -8px",
            boxSizing: "border-box",
            verticalAlign: "baseline",
            lineHeight: "1",
            display: "inline-block",
          }}
        />
      ) : (
        <span
          onClick={handleSecondsClick}
          style={{
            cursor: isMetronomeRunning ? "default" : "pointer",
            userSelect: isMetronomeRunning ? "none" : "auto",
            transition: "all 0.2s ease-out",
            display: "inline-block",
            transformOrigin: "center",
            padding: "4px 8px",
            borderRadius: "8px",
            margin: "-4px -8px",
          }}
          onMouseEnter={(e) => {
            if (!isMetronomeRunning) {
              e.currentTarget.style.backgroundColor = "var(--button-hover-bg)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          {displaySeconds}
        </span>
      )}
    </div>
  );
};
